import * as Yup from "yup";

// Helper function to create language test score validation
const createLanguageTestSchema = () =>
  Yup.object({
    overall: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .test(
        "required-if-others-filled",
        "Overall score is required when other scores are filled",
        function (value) {
          const { listening, reading, speaking, writing } = this.parent;
          const hasOtherFields =
            listening !== null ||
            reading !== null ||
            speaking !== null ||
            writing !== null;

          if (!hasOtherFields) return true;
          if (value === null || value === undefined) return false;
          return value >= 0;
        },
      ),
    listening: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .test(
        "required-if-others-filled",
        "Listening score is required when other scores are filled",
        function (value) {
          const { overall, reading, listening, speaking, writing } =
            this.parent;
          const hasOtherFields =
            overall !== null ||
            listening !== null ||
            reading !== null ||
            speaking !== null ||
            writing !== null;

          if (!hasOtherFields) return true;
          if (value === null || value === undefined) return false;
          return value >= 0;
        },
      ),
    reading: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .test(
        "required-if-others-filled",
        "Reading score is required when other scores are filled",
        function (value) {
          const { overall, listening, speaking, writing } = this.parent;
          const hasOtherFields =
            overall !== null ||
            listening !== null ||
            speaking !== null ||
            writing !== null;

          if (!hasOtherFields) return true;
          if (value === null || value === undefined) return false;
          return value >= 0;
        },
      ),
    speaking: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .test(
        "required-if-others-filled",
        "Speaking score is required when other scores are filled",
        function (value) {
          const { overall, listening, reading, writing } = this.parent;
          const hasOtherFields =
            overall !== null ||
            listening !== null ||
            reading !== null ||
            writing !== null;

          if (!hasOtherFields) return true;
          if (value === null || value === undefined) return false;
          return value >= 0;
        },
      ),
    writing: Yup.number()
      .nullable()
      .transform((value, originalValue) =>
        originalValue === "" ? null : value,
      )
      .test(
        "required-if-others-filled",
        "Writing score is required when other scores are filled",
        function (value) {
          const { overall, listening, reading, speaking } = this.parent;
          const hasOtherFields =
            overall !== null ||
            listening !== null ||
            reading !== null ||
            speaking !== null;

          if (!hasOtherFields) return true;
          if (value === null || value === undefined) return false;
          return value >= 0;
        },
      ),
  }).nullable();

export const courseSchema = [
  // Stage 0: Basic Course Information + Institution & Location
  Yup.object({
    cricosCourseCode: Yup.string()
      .required("CRICOS Course Code is required")
      .matches(
        /^\d{6}[A-Z]$/,
        "CRICOS Course Code must be 6 digits followed by 1 uppercase letter (e.g., 083010M)",
      ),
    courseName: Yup.string().required("Please enter a course name"),
    level: Yup.string().required("Please choose a level"),
    programType: Yup.string().required("Please choose a program type"),
    description: Yup.string(),
    url: Yup.string()
      .nullable()
      .test(
        "is-valid-url",
        "Please enter a valid URL (e.g., www.example.com or example.com). Protocols like 'http://' are not allowed.",
        (value) => {
          if (!value) return true;
          const urlRegex = /^(www\.)?[^\s]+\.[a-z]{2,}(\/[^\s]*)?$/i;
          return urlRegex.test(value);
        },
      ),
    institutionId: Yup.string().required("Please enter an institution URL"),
    institutionName: Yup.string().required("Please enter an institution name"),
    country: Yup.string().required("Please choose a country"),
    address: Yup.array()
      .of(Yup.string())
      .test(
        "at-least-one-address",
        "At least one address is required",
        (value) => {
          if (!value) return false;
          return value.some((addr) => addr && addr.trim().length > 0);
        },
      )
      .required("Please enter at least one address"),
  }),

  // Stage 1: Course Structure (no substages now)
  Yup.object({
    courseCode: Yup.string(),
    fieldOfStudy: Yup.array()
      .of(Yup.string())
      .min(1, "At least one field of study is required")
      .required("Field of study is required"),
    modeOfStudy: Yup.string().required("Please select a mode of study"),
    startDate: Yup.date().nullable().required("Start date is required"),
    durationDay: Yup.number()
      .min(0, "Cannot be negative")
      .required("Duration is required"),
  }),

  // Stage 2: Language Requirements
  Yup.object({
    languageTests: Yup.object({
      ielts: createLanguageTestSchema(),
      toefl: createLanguageTestSchema(),
      pte: createLanguageTestSchema(),
      cae: createLanguageTestSchema(),
      cpe: createLanguageTestSchema(),
    }),
  }),

  // Stage 3: Fees and Career
  Yup.object({
    tuitionFee: Yup.number()
      .min(0, "Cannot be negative")
      .required("Tuition fee is required"),
    nonTuitionFee: Yup.number().min(0, "Cannot be negative"),
    estimatedTotalCourseFee: Yup.number()
      .min(0, "Cannot be negative")
      .required("Estimated total course fee is required"),
    careerProspect: Yup.string(),
  }),

  // Stage 4: Review & Publish
  Yup.object({}),
];
